import { Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { ContentBox, StyledDivider } from './StudyDetail.styles';
import StudyDetailList from './StudyDetailList';
import formatDate from '../../lib/utils/date/formatDate';
import getAgeRange from '../../lib/utils/coveo/getAgeRange';
import UiHeading from '../shared/Heading';
import Head from 'next/head';

type StudyDetailProps = ComponentProps & {
  fields: {
    ageLabel: Field<string>;
    completionDateLabel: Field<string>;
    conditionLabel: Field<string>;
    ctGovIdLabel: Field<string>;
    investigationalProductLabel: Field<string>;
    participantLabel: Field<string>;
    phaseLabel: Field<string>;
    sectionTitle: Field<string>;
    sponsorLabel: Field<string>;
    studyNymberLabel: Field<string>;
    treatmentTypeLabel: Field<string>;
  };
};

export type TrialConditionsType = {
  fields: {
    conditionValue: Field<string>;
  };
};

export type FieldValues = {
  minimumAge: Field<string>;
  maximumAge: Field<string>;
  phase: Field<string>;
  enrollment: Field<string>;
  completionDate: Field<string>;
  studyType: Field<string>;
  investigationalProduct: Field<string>;
  source: Field<string>;
  nctId: Field<string>;
  orgStudyId: Field<string>;
  trialConditions: TrialConditionsType[];
  trialTitle: Field<string>;
  trialStatus: Field<string>;
  trialDescription: Field<string>;
};
const StudyDetail = (props: StudyDetailProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();

  if (!props.fields) {
    return <></>;
  }
  const fieldValues = sitecoreContext?.route?.fields as unknown as FieldValues;
  const fieldLabels = props?.fields;

  const trials = {
    value: fieldValues?.trialConditions
      ?.map((x: TrialConditionsType) => x.fields?.conditionValue.value)
      .join(','),
  };
  const age = {
    value: getAgeRange(fieldValues?.minimumAge?.value, fieldValues?.maximumAge?.value),
  };
  const formattedDate = {
    value:
      fieldValues?.completionDate?.value &&
      fieldValues?.completionDate?.value !== '0001-01-01T00:00:00Z'
        ? formatDate(fieldValues?.completionDate?.value)
        : '',
  };
  const renderJSONLDSchema = () => {
    // JSON-LD structured data
    const schemaData = {
      '@context': 'https://schema.org/',
      '@type': 'MedicalStudy',
      name: fieldValues?.trialTitle?.value,
      status:
        fieldValues?.trialStatus?.value === 'Enrollment'
          ? 'https://schema.org/Recruiting'
          : `https://schema.org/${fieldValues?.trialStatus?.value}`,
      studySubject: {
        '@type': 'MedicalCondition',
        name: trials?.value,
      },
      sponsor: {
        '@type': 'Organization',
        name: fieldValues?.source?.value,
      },
      identifier: fieldValues?.nctId?.value,
      description: fieldValues?.trialDescription?.value,
      url: typeof window !== 'undefined' ? window.location.href : null,
    };

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
    );
  };
  return (
    <>
      {renderJSONLDSchema()}
      <ContentBox>
        <UiHeading size="h3" data-testid="title" color="primary" align="center" isChildren>
          <Text field={fieldLabels?.sectionTitle} />
        </UiHeading>
        <StyledDivider />
        <StudyDetailList label={fieldLabels.conditionLabel} value={trials} />
        <StudyDetailList label={fieldLabels.ageLabel} value={age} />
        <StudyDetailList label={fieldLabels.phaseLabel} value={fieldValues?.phase} />
        <StudyDetailList label={fieldLabels.participantLabel} value={fieldValues?.enrollment} />
        <StudyDetailList label={fieldLabels.completionDateLabel} value={formattedDate} />
        <StudyDetailList label={fieldLabels.treatmentTypeLabel} value={fieldValues?.studyType} />
        <StudyDetailList
          label={fieldLabels.investigationalProductLabel}
          value={fieldValues?.investigationalProduct}
        />
        <StyledDivider />
        <StudyDetailList label={fieldLabels.sponsorLabel} value={fieldValues?.source} />
        <StudyDetailList label={fieldLabels.ctGovIdLabel} value={fieldValues?.nctId} />
        <StudyDetailList label={fieldLabels.studyNymberLabel} value={fieldValues?.orgStudyId} />
      </ContentBox>
    </>
  );
};

export default StudyDetail;
